import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useGoBack } from "../../../hooks/useGoBack";
import {
  DAY_FLAG_LABELS,
  DAY_KIND_LABELS,
  duration,
  getMyTimesheetApi,
  hhmm,
  MyTimesheet,
  MyTimesheetDay,
  timeOf,
} from "../api/timekeepingApi";
import AdjustmentSheet from "../components/AdjustmentSheet";
import EmployeeShell from "../components/EmployeeShell";
import SignTimesheetSheet from "../components/SignTimesheetSheet";
import { COLABORADOR_AJUSTES, COLABORADOR_HOME } from "../routes";

import "./Colaborador.css";
import "./Ponto.css";

const REST = new Set(["FOLGA", "FERIADO", "LOJA_FECHADA", "FERIAS", "AFASTAMENTO", "SEM_ESCALA"]);
/** Avisos que só informam (não pedem correção). */
const INFO_FLAGS = new Set(["COM_AJUSTE", "SEM_ESCALA_PUBLICADA", "INTERVALO_PRESUMIDO", "TRABALHO_EM_DESCANSO", "FORA_DO_PERIODO"]);

const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", { timeZone: "UTC", weekday: "short", day: "2-digit", month: "2-digit" });

/**
 * O espelho de ponto do mês, do jeito que o DP vê: o previsto pela escala, as
 * marcações (com as que vieram de ajuste marcadas), horas e faltas. Dia com
 * problema tem "Corrigir", que vira um pedido ao DP.
 */
export default function MyTimesheetPage() {
  const goBack = useGoBack(COLABORADOR_HOME());
  const navigate = useNavigate();
  const now = new Date();
  // `?ano=2026&mes=9` (link da faixa "confira e assine" da home).
  const [params] = useSearchParams();
  const qYear = Number(params.get("ano"));
  const qMonth = Number(params.get("mes"));
  const [ym, setYm] = useState(
    qYear > 2000 && qMonth >= 1 && qMonth <= 12 ? { year: qYear, month: qMonth } : { year: now.getFullYear(), month: now.getMonth() + 1 },
  );
  const [signing, setSigning] = useState<"ASSINAR" | "CONTESTAR" | null>(null);
  const [data, setData] = useState<MyTimesheet | null>(null);
  const [fixing, setFixing] = useState<MyTimesheetDay | null>(null);
  const [onlyAttention, setOnlyAttention] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    try {
      setData(await getMyTimesheetApi(ym.year, ym.month));
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível carregar o espelho.");
      setData({ enabled: false, startDate: null, year: ym.year, month: ym.month, timesheet: null });
    }
  }, [ym]);

  useEffect(() => {
    load();
  }, [load]);

  const shiftMonth = (delta: number) => {
    const d = new Date(ym.year, ym.month - 1 + delta, 1);
    setYm({ year: d.getFullYear(), month: d.getMonth() + 1 });
  };

  const sheet = data?.timesheet ?? null;
  const pendingByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of data?.adjustments ?? []) if (a.status === "PENDENTE") map.set(a.day.slice(0, 10), (map.get(a.day.slice(0, 10)) ?? 0) + 1);
    return map;
  }, [data]);

  const today = todayIso();
  const days = (sheet?.days ?? []).filter((d) => !d.flags.includes("FORA_DO_PERIODO") && (!onlyAttention || d.needsAttention));
  const attention = (sheet?.days ?? []).filter((d) => d.needsAttention).length;
  const monthName = new Date(ym.year, ym.month - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  const t = sheet?.totals;

  return (
    <EmployeeShell>
      <section className="colabPanel">
        <div className="colabPageTitle">
          <h2 className="colabPanel__title" style={{ margin: 0 }}>
            Meu espelho de ponto
          </h2>
          <button type="button" className="colabBtn colabBtn--ghost colabBtn--compact" onClick={goBack}>
            Voltar
          </button>
        </div>

        <div className="colabMonthNav">
          <button type="button" className="colabBtn colabBtn--ghost colabBtn--icon" onClick={() => shiftMonth(-1)} aria-label="Mês anterior">
            ‹
          </button>
          <strong>{monthName}</strong>
          <button type="button" className="colabBtn colabBtn--ghost colabBtn--icon" onClick={() => shiftMonth(1)} aria-label="Próximo mês">
            ›
          </button>
        </div>

        {data === null ? (
          <p className="colabLoading">Carregando...</p>
        ) : !data.enabled ? (
          <p className="colabHint">O ponto eletrônico ainda não foi liberado.</p>
        ) : !sheet ? (
          <p className="colabHint">Não há ponto seu neste mês.</p>
        ) : (
          <>
            {sheet.status === "FECHADO" ? (
              <SignatureBlock sheet={sheet} onSign={() => setSigning("ASSINAR")} onContest={() => setSigning("CONTESTAR")} />
            ) : sheet.signature?.status === "DESATUALIZADO" ? (
              <p className="colabNotice colabNotice--warn">
                O DP reabriu este espelho depois da sua assinatura. Quando ele fechar de novo, confira e assine a nova versão.
              </p>
            ) : attention ? (
              <p className="colabNotice colabNotice--warn">
                {attention} dia(s) precisam de correção. Toque em "Corrigir" no dia para pedir ao DP.
              </p>
            ) : null}

            {t ? (
              <div className="colabTotals">
                <div className="colabTotal">
                  <span>Trabalhado</span>
                  <strong>{duration(t.workedMinutes)}</strong>
                </div>
                <div className="colabTotal">
                  <span>Previsto</span>
                  <strong>{duration(t.expectedMinutes)}</strong>
                </div>
                <div className="colabTotal colabTotal--good">
                  <span>Horas extras</span>
                  <strong>{duration(t.overtimeWorkdayMinutes + t.overtimeRestDayMinutes)}</strong>
                </div>
                <div className={`colabTotal ${t.absenceDays ? "colabTotal--bad" : ""}`}>
                  <span>Faltas</span>
                  <strong>{t.absenceDays}</strong>
                </div>
                <div className={`colabTotal ${t.lateMinutes + t.earlyLeaveMinutes ? "colabTotal--bad" : ""}`}>
                  <span>Atrasos e saídas antes</span>
                  <strong>{duration(t.lateMinutes + t.earlyLeaveMinutes)}</strong>
                </div>
                <div className="colabTotal">
                  <span>Adicional noturno</span>
                  <strong>{duration(t.nightComputedMinutes)}</strong>
                </div>
                <div className="colabTotal">
                  <span>Dias trabalhados</span>
                  <strong>{t.workedDaysCount}</strong>
                </div>
                <div className={`colabTotal ${t.dsrLostCount ? "colabTotal--bad" : ""}`}>
                  <span>Descansos perdidos</span>
                  <strong>{t.dsrLostCount}</strong>
                </div>
              </div>
            ) : null}

            <div className="colabSegmented" role="group" aria-label="Filtro">
              <button type="button" className={!onlyAttention ? "is-on" : ""} onClick={() => setOnlyAttention(false)}>
                Todos os dias
              </button>
              <button type="button" className={onlyAttention ? "is-on" : ""} onClick={() => setOnlyAttention(true)}>
                Para corrigir ({attention})
              </button>
              <button type="button" onClick={() => navigate(COLABORADOR_AJUSTES())}>
                Meus pedidos
              </button>
            </div>

            <div className="colabDays">
              {days.map((d) => {
                const key = d.day.slice(0, 10);
                const pending = pendingByDay.get(key) ?? 0;
                const rest = REST.has(d.kind) && !d.effectivePunches.length;
                return (
                  <article
                    key={d.id}
                    className={`colabDay ${d.needsAttention ? "colabDay--attention" : ""} ${rest ? "colabDay--rest" : ""} ${key === today ? "colabDay--today" : ""}`}
                  >
                    <div className="colabDay__head">
                      <span className="colabDay__date">{formatDay(d.day)}</span>
                      <span className={`colabBadge ${d.kind === "FALTA" ? "colabBadge--orange" : d.kind === "TRABALHO" ? "colabBadge--green" : "colabBadge--gray"}`}>
                        {DAY_KIND_LABELS[d.kind]}
                        {d.dsrLost ? " · perdeu o descanso" : ""}
                      </span>
                    </div>

                    {d.startMinute !== null && (d.kind === "TRABALHO" || d.kind === "FALTA") ? (
                      <span className="colabDay__line">
                        Previsto {hhmm(d.startMinute)}–{hhmm(d.endMinute)} ({duration(d.expectedMinutes)})
                      </span>
                    ) : d.expectedMinutes ? (
                      <span className="colabDay__line">Previsto {duration(d.expectedMinutes)}</span>
                    ) : null}

                    {d.effectivePunches.length ? (
                      <div className="colabDay__marks">
                        {d.effectivePunches.map((p) => (
                          <span key={p.at} className={`colabDay__mark ${p.source === "AJUSTE" ? "colabDay__mark--adj" : ""}`} title={p.source === "AJUSTE" ? "Incluída por ajuste" : `Nº ${p.nsr}`}>
                            {timeOf(p.at)}
                            {p.source === "AJUSTE" ? "*" : ""}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    {d.workedMinutes || d.overtimeWorkdayMinutes || d.overtimeRestDayMinutes || d.lateMinutes || d.earlyLeaveMinutes ? (
                      <span className="colabDay__line">
                        Trabalhado {duration(d.workedMinutes)}
                        {d.overtimeWorkdayMinutes + d.overtimeRestDayMinutes ? ` · extra ${duration(d.overtimeWorkdayMinutes + d.overtimeRestDayMinutes)}` : ""}
                        {d.lateMinutes ? ` · atraso ${duration(d.lateMinutes)}` : ""}
                        {d.earlyLeaveMinutes ? ` · saiu antes ${duration(d.earlyLeaveMinutes)}` : ""}
                      </span>
                    ) : null}

                    {d.flags.filter((f) => DAY_FLAG_LABELS[f]).length ? (
                      <div className="colabDay__flags">
                        {d.flags
                          .filter((f) => DAY_FLAG_LABELS[f])
                          .map((f) => (
                            <span key={f} className={`colabDay__flag ${INFO_FLAGS.has(f) ? "colabDay__flag--info" : ""}`}>
                              {DAY_FLAG_LABELS[f]}
                            </span>
                          ))}
                      </div>
                    ) : null}

                    {pending ? <span className="colabDay__line">{pending} pedido(s) aguardando o DP.</span> : null}

                    {d.canRequestAdjustment ? (
                      <div className="colabDay__actions">
                        <button
                          type="button"
                          className={`colabBtn ${d.needsAttention ? "colabBtn--primary" : "colabBtn--ghost"} colabBtn--compact`}
                          onClick={() => setFixing(d)}
                        >
                          Corrigir
                        </button>
                      </div>
                    ) : null}
                  </article>
                );
              })}
              {!days.length ? <p className="colabHint">Nada para mostrar.</p> : null}
            </div>

            <p className="colabFooterNote">* Marcação incluída por pedido aprovado pelo DP.</p>
          </>
        )}
      </section>

      {signing ? (
        <SignTimesheetSheet
          mode={signing}
          year={ym.year}
          month={ym.month}
          onClose={() => setSigning(null)}
          onDone={() => {
            toast.success(signing === "ASSINAR" ? "Espelho assinado." : "Contestação enviada ao DP.");
            setSigning(null);
            load();
          }}
        />
      ) : null}

      {fixing ? (
        <AdjustmentSheet
          initial={{ day: fixing.day.slice(0, 10), dayData: fixing }}
          onClose={() => setFixing(null)}
          onSent={() => {
            setFixing(null);
            toast.success("Pedido enviado ao DP.");
            load();
          }}
        />
      ) : null}
    </EmployeeShell>
  );
}

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** Espelho fechado: assinar, contestar, ou o selo do que já foi feito. */
function SignatureBlock({
  sheet,
  onSign,
  onContest,
}: {
  sheet: NonNullable<MyTimesheet["timesheet"]>;
  onSign: () => void;
  onContest: () => void;
}) {
  const sig = sheet.signature;
  const closed = `Mês fechado pelo DP${sheet.closedAt ? ` em ${new Date(sheet.closedAt).toLocaleDateString("pt-BR")}` : ""}.`;
  if (!sig || sig.status === "ASSINADO") {
    return (
      <p className="colabNotice colabNotice--ok">
        {closed} {sig?.signedAt ? <strong>Assinado por você em {dateTime(sig.signedAt)}.</strong> : null}
      </p>
    );
  }
  return (
    <div className={`colabNotice ${sig.status === "CONTESTADO" ? "colabNotice--warn" : "colabNotice--ok"} colabSignBlock`}>
      <p style={{ margin: 0 }}>
        {closed}{" "}
        {sig.status === "CONTESTADO" ? (
          <>
            <strong>Você contestou em {dateTime(sig.lastContest?.createdAt ?? "")}</strong> e o DP foi avisado: "{sig.lastContest?.text}". Se o DP corrigir, o
            espelho volta para você assinar; se estiver tudo certo, você pode assinar agora.
          </>
        ) : sig.status === "DESATUALIZADO" ? (
          <strong>O espelho mudou depois da sua assinatura; confira e assine de novo.</strong>
        ) : (
          <strong>Confira os dias abaixo e assine.</strong>
        )}
      </p>
      <div className="colabSignBlock__actions">
        <button type="button" className="colabBtn colabBtn--primary colabBtn--compact" onClick={onSign}>
          Concordo e assino
        </button>
        <button type="button" className="colabBtn colabBtn--ghost colabBtn--compact" onClick={onContest}>
          Contestar
        </button>
      </div>
    </div>
  );
}
